import React, { useState } from "react";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";
import AIChatbotSettingsRow from "./AIChatbotSettingsRow";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";
import { IconTrash } from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import AICrawlingModal from "./AICrawlingModal";
import { WebTrainingDetails } from "../types";
import AICrawlResult from "./AICrawlResult";
import AIChatbotSettingsTrainingTabFile from "./AIChatbotFileTrainingIndex";

export interface WebCrawlingResults {
  url: string;
  success: boolean;
  content: string;
}

export default function AIChatbotWebTrainingIndex() {
  const [trainingUrl, setTrainingUrl] = useState("");
  const [urlList, setUrlList] = useState<string[]>([]);
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const toastAlert = useToastAlert();
  const [status, setStatus] = useState("ready");
  const [crawlerResult, setCrawlerResult] = useState<WebCrawlingResults[]>();
  const [webCrawlDetails, setWebCrawlDetails] = useState<WebTrainingDetails>({
    name: "Web Crawl",
    urls: [],
    crawlDepth: 1,
    maxPages: 10,
    includeExternal: false,
    excludedCssSelectors: "",
    aiCrawl: false,
    aiCrawlPrompt: "",
    crawlResultDelay: 0,
    crawlUrlExclusion: "",
    crawlUrlFilter: ""
  });
  const api = useApi();

  async function addUrl() {
    if (trainingUrl.length === 0) {
      toastAlert.displayToast(Severity.Error, "Please enter a URL");
      return;
    }
    if (urlList.includes(trainingUrl)) {
      toastAlert.displayToast(Severity.Error, "URL already exists in list");
      setTrainingUrl("");
      return;
    }
    const urls = [...urlList];
    urls.push(trainingUrl);
    setUrlList(urls);
  }

  async function deleteUrl(url: string) {
    let urls = [...urlList];
    urls = urls.filter((u) => u !== url);
    setUrlList(urls);
  }

  async function deleteResult(index: number) {
    if (!crawlerResult) return;
    const results = [...crawlerResult];
    const deletedUrl = results[index]?.url;
    results.splice(index, 1);
    toastAlert.displayToast(
      Severity.Success,
      `Deleted result ${index}: ${deletedUrl}`
    );
    setCrawlerResult(results);
    if (results.length === 0) {
      setStatus("ready");
    }
  }

  async function webCrawl(crawlerDetails: WebTrainingDetails) {
    console.log("start web crawl");
    setStatus("crawling");
    setWebCrawlDetails(crawlerDetails);
    const response = await api.messaging.post(
      "WebScraping/WebCrawl",
      crawlerDetails
    );
    if (response.ok) {
      const json = await response.json();
      setCrawlerResult(json);
      setStatus("ready");
      toastAlert.displayToast(Severity.Success, "Web crawl completed");
    } else {
      const json = await response.json();
      console.log("web crawl error", json);
      setStatus("error");
      toastAlert.displayToast(Severity.Error, "Web crawl failed");
    }
  }

  async function clearTrainingModal() {
    setShowTrainingModal(false);
  }

  async function saveCrawlingResults() {
    if (!crawlerResult || crawlerResult.length === 0) {
      toastAlert.displayToast(Severity.Error, "No results to save");
      return;
    }
    setStatus("saving");
    const response = await api.messaging.post("WebScraping/SaveResults", {
      WebCrawlingResults: crawlerResult,
      CrawlName: webCrawlDetails.name
    });
    if (response.ok) {
      toastAlert.displayToast(
        Severity.Success,
        "Crawling results saved successfully"
      );
      setCrawlerResult(undefined);
      setUrlList([]);
      setStatus("ready");
    } else {
      const json = await response.json();
      console.error("Error saving crawling results", json);
      toastAlert.displayToast(
        Severity.Error,
        "Failed to save crawling results"
      );
    }
  }

  return (
    <>
      <input
        type="text"
        value={trainingUrl}
        onChange={(e) => setTrainingUrl(e.target.value)}
        placeholder="Enter a url"
        className="w-full p-2 border-[1px] rounded-md text-sm mt-4"
      />
      <div className="flex items-center mt-1">
        <input type="checkbox" className="p-2 text-sm" />
        <label className="text-slate-500 text-sm p-2">
          Fetch all links within this page
        </label>
      </div>
      <div className="flex-row flex justify-end gap-4">
        <div className="flex justify-end">
          <PrimaryBlueButton
            label="Add URL"
            onClick={() => addUrl()}
            disabled={trainingUrl.length === 0}
          />
        </div>
        <div className="flex justify-end">
          <PrimaryBlueButton
            label="Train AI"
            disabled={urlList.length === 0 || status == "crawling"}
            onClick={() => setShowTrainingModal(true)}
          />
        </div>
      </div>
      <div>
        <AIChatbotSettingsRow
          title="Urls"
          subtitle="URL's the bot will be trained on"
          input={
            <>
              <div className="ml-2 text-slate-500">
                {urlList.map((url, index) => (
                  <div key={index}>
                    {url}
                    <button onClick={() => deleteUrl(url)}>
                      <IconTrash className="flex ml-2 w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </>
          }
        />
      </div>
      <div>
        <AIChatbotSettingsRow
          title="Training Status"
          subtitle=""
          input={
            <>
              <div className="flex flex-row items-center">
                <div className="mx-2">{status}</div>
                {status != "ready" && <LoadingSpinner />}
              </div>
            </>
          }
        />
      </div>
      {showTrainingModal && (
        <AICrawlingModal
          urls={urlList}
          clearTrainingModal={clearTrainingModal}
          webCrawl={webCrawl}
        />
      )}
      {crawlerResult && status != "saving" && (
        <div className="">
          <div className="font-semibold text-lg pb-2">Crawling Results:</div>
          <div className="h-[450px] overflow-y-scroll">
            {crawlerResult.map((result, index) => (
              <AICrawlResult
                key={result.url}
                index={index}
                url={result.url}
                success={result.success}
                content={result.content}
                deleteResult={() => deleteResult(index)}
              />
            ))}
          </div>
          <div className="mt-4">
            <PrimaryBlueButton
              label="Save Results"
              onClick={() => saveCrawlingResults()}
            />
          </div>
        </div>
      )}
      {status != "saving" && <AIChatbotSettingsTrainingTabFile />}
    </>
  );
}
